import { computed, ref } from 'vue';
import { trainingMatrixApi } from '@/entities/Training';
import type { TrainingMatrixRevisionListItem } from '@/entities/Training';
import { useCustomToast } from '@/shared/lib';

const REVISIONS_ITEMS_PER_PAGE = 100;

export function useTrainingMatrixRevisionSelection() {
  const toast = useCustomToast();

  const isModalOpen = ref(false);
  const loading = ref(false);
  const revisions = ref<TrainingMatrixRevisionListItem[]>([]);
  const selectedRevisionId = ref<number | null>(null);

  const hasAnyRevision = computed(() => revisions.value.length > 0);
  const canConfirm = computed(() => selectedRevisionId.value !== null);

  async function fetchRevisions(): Promise<void> {
    loading.value = true;
    try {
      const collected: TrainingMatrixRevisionListItem[] = [];
      let page = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const result = await trainingMatrixApi.listRevisions({
          page,
          itemsPerPage: REVISIONS_ITEMS_PER_PAGE,
        });
        collected.push(...result.data);
        hasNextPage = result.metadata.isNextPage;
        page += 1;
      }

      revisions.value = collected;
    } catch {
      toast.showErrorToast({
        title: 'Erro ao carregar revisões',
        message: 'Não foi possível carregar a lista de revisões. Tente novamente.',
        type: 'error',
      });
    } finally {
      loading.value = false;
    }
  }

  function selectRevision(revisionId: number): void {
    selectedRevisionId.value = revisionId;
  }

  function openModal(): void {
    selectedRevisionId.value = null;
    isModalOpen.value = true;
    void fetchRevisions();
  }

  function closeModal(): void {
    isModalOpen.value = false;
    selectedRevisionId.value = null;
  }

  return {
    isModalOpen,
    loading,
    revisions,
    selectedRevisionId,
    hasAnyRevision,
    canConfirm,
    fetchRevisions,
    selectRevision,
    openModal,
    closeModal,
  };
}
